import { useEffect, useRef, useState } from "react";
import { AlertCircle } from "lucide-react";
import Button from "../common/Button";
import Input from "../common/Input";
import CustomSelect from "../common/CustomSelect";
import FileUpload from "../common/FileUpload";
import { updateNews } from "../../../services/newsServices";
import { toast } from "react-hot-toast";
import RichTextEditor from "../common/RichTextEditor";
import { useNavigate } from "react-router";

const publicOptions = [
  { value: "public", label: "Public" },
  { value: "private", label: "Private" },
];

const galleryNameOptions = [
  { value: "yes", label: "Yes" },
  { value: "no", label: "No" },
];

const statusOptions = [
  { value: "pending", label: "pending" },
  { value: "approved", label: "approved" },
  { value: "rejected", label: "rejected" },
];

const EditNewsForm = ({ news, id }) => {
  const imageRef = useRef();
  const navigate = useNavigate();
  const [title, setTitle] = useState("");
  const [date, setDate] = useState("");
  const [galleryName, setGalleryName] = useState(galleryNameOptions[0]);
  const [description, setDescription] = useState("");
  const [status, setStatus] = useState(null);
  const [visibility, setVisibility] = useState(null);
  const [image, setImage] = useState([]);
  const [loading, setLoading] = useState(false);

  // Field-level errors
  const [errors, setErrors] = useState({});

  useEffect(() => {
    if (news) {
      setTitle(news.title || "");
      setDate(news.date ? news.date.split("T")[0] : "");
      setGalleryName(
        galleryNameOptions.find((opt) =>
          news.showOnHomepage ? opt.value === "yes" : opt.value === "no"
        ) || galleryNameOptions[0]
      );
      setDescription(news.description || "");
      setStatus(
        news.status
          ? statusOptions.find((opt) => opt.value === news.status)
          : null
      );
      setVisibility(
        publicOptions.find((opt) => opt.value === news.visibility) || null
      );
      setImage(news.imageUrl || []);
    }
  }, [news]);

  const validate = () => {
    const newErrors = {};
    if (!title) newErrors.title = "Title is required";
    if (!date) newErrors.date = "Date is required";
    if (!description) newErrors.description = "Description is required";
    if (!galleryName) newErrors.galleryName = "Please select an option";
    if (!visibility) newErrors.visibility = "Please select visibility";
    if (!status) newErrors.status = "Please select status";

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const buildPayload = () => ({
    title,
    description,
    date,
    showOnHomepage: galleryName.value === "yes",
    imageUrl: image,
    visibility: visibility.value,
    status: status ? status.value : "approved",
  });

  const handleSubmit = async () => {
    if (!validate()) return;

    const payload = buildPayload();

    try {
      setLoading(true);
      await updateNews(id, payload);
      toast.success("News Updated successfully!");
      window.location.href = "/admin/news/list";
    } catch (error) {
      toast.error(error.message || "Failed to update news");
    } finally {
      setLoading(false);
    }
  };

  const handleImageDelete = async (updatedImages) => {
    const payload = buildPayload();
    payload.imageUrl = updatedImages;
    try {
      await updateNews(id, payload);
    } catch (error) {
      toast.error(error.message || "Failed to delete image");
      throw error;
    }
  };

  return (
    <div className="bg-[#F5F0FF] p-6 rounded-lg font-sans space-y-4">
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div>
          <Input
            label="Title"
            value={title}
            onChange={(val) => {
              setTitle(val);
              if (errors.title) setErrors((prev) => ({ ...prev, title: "" }));
            }}
            required
          />
          {errors.title && (
            <p className="text-red-600 font-semibold text-xs mt-1 flex items-center gap-1">
              <AlertCircle size={12} />
              {errors.title}
            </p>
          )}
        </div>
        <div>
          <Input
            label="Date"
            type="date"
            value={date}
            onChange={(val) => {
              setDate(val);
              if (errors.date) setErrors((prev) => ({ ...prev, date: "" }));
            }}
            required
          />
          {errors.date && (
            <p className="text-red-600 font-semibold text-xs mt-1 flex items-center gap-1">
              <AlertCircle size={12} />
              {errors.date}
            </p>
          )}
        </div>
        <div>
          <CustomSelect
            label="Show on Home page ?"
            value={galleryName}
            onChange={(val) => {
              setGalleryName(val);
              if (errors.galleryName)
                setErrors((prev) => ({ ...prev, galleryName: "" }));
            }}
            options={galleryNameOptions}
            placeholder="Yes / No"
          />
          {errors.galleryName && (
            <p className="text-red-600 font-semibold text-xs mt-1 flex items-center gap-1">
              <AlertCircle size={12} />
              {errors.galleryName}
            </p>
          )}
        </div>
      </div>

      <div>
        <RichTextEditor
          description={description}
          setDescription={(val) => {
            setDescription(val);
            if (errors.description)
              setErrors((prev) => ({ ...prev, description: "" }));
          }}
        />
        {errors.description && (
          <p className="text-red-600 font-semibold text-xs mt-1 flex items-center gap-1">
            <AlertCircle size={12} />
            {errors.description}
          </p>
        )}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div>
          <CustomSelect
            label="Public / Private"
            value={visibility}
            onChange={(val) => {
              setVisibility(val);
              if (errors.visibility)
                setErrors((prev) => ({ ...prev, visibility: "" }));
            }}
            options={publicOptions}
            placeholder="Select"
          />
          {errors.visibility && (
            <p className="text-red-600 font-semibold text-xs mt-1 flex items-center gap-1">
              <AlertCircle size={12} />
              {errors.visibility}
            </p>
          )}
        </div>
        <div>
          <CustomSelect
            label="Status"
            value={status}
            onChange={(val) => {
              setStatus(val);
              if (errors.status) setErrors((prev) => ({ ...prev, status: "" }));
            }}
            options={statusOptions}
            placeholder="Select Status"
          />
          {errors.status && (
            <p className="text-red-600 font-semibold text-xs mt-1 flex items-center gap-1">
              <AlertCircle size={12} />
              {errors.status}
            </p>
          )}
        </div>
      </div>

      <div>
        <FileUpload
          ref={imageRef}
          mode="image"
          defaultLabel="Choose Images"
          multiple={true}
          onUpload={() => {}}
          onDelete={handleImageDelete}
          image={image}
          setImage={setImage}
        />
        {errors.image && (
          <p className="text-red-600 font-semibold text-xs mt-1 flex items-center gap-1">
            <AlertCircle size={12} />
            {errors.image}
          </p>
        )}
      </div>

      <div className="pt-4 space-y-2">
        <Button
          className="w-full"
          type="button"
          onClick={handleSubmit}
          disabled={loading}
        >
          {loading ? "Updating..." : "Update News"}
        </Button>
        <Button
          onClick={() => navigate("/admin/news/list")}
          className="w-full"
          variant="text"
        >
          Cancel
        </Button>
      </div>
    </div>
  );
};

export default EditNewsForm;
